import { NgTemplateOutlet } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, contentChild, effect, EventEmitter, inject, input, InputSignal, Output, signal, Signal, TemplateRef, WritableSignal } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { FaIconLibrary } from '@fortawesome/angular-fontawesome';
import { faMinusSquare, faPlusSquare } from '@fortawesome/pro-light-svg-icons';
import { IconModule } from '@ogs-gmbh/ngx-m3-components';
import { OgsItemDirective } from '@shared/directives/ogs-item.directive';
import { DynamicFormFieldListCreateFormControl } from './types/dynamic-form-field-list-create-form-control.type';
import { FormArray, FormControl, FormControlStatus } from '@angular/forms';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';
import { combineLatest, distinctUntilChanged, map, merge, Observable, of, startWith, switchMap, tap } from 'rxjs';

const DEFAULT_MIN_VISIBLE_ITEMS: number = 1;

@Component({
  selector: 'app-dynamic-form-field-list',
  standalone: true,
  imports: [ NgTemplateOutlet, MatButtonModule, IconModule ],
  templateUrl: './dynamic-form-field-list.component.html',
  styleUrl: './dynamic-form-field-list.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class DynamicFormFieldListComponent {
  private readonly _faIconLibrary: FaIconLibrary = inject(FaIconLibrary);

  protected readonly template: Signal<TemplateRef<unknown> | undefined> = contentChild(OgsItemDirective, { read: TemplateRef });

  @Output() public readonly itemRemoved: EventEmitter<number> = new EventEmitter<number>();

  @Output() public readonly itemAdded: EventEmitter<void> = new EventEmitter<void>();

  /**
   * Maximum number of FormFields.
   */
  public readonly maxItems: InputSignal<number | undefined> = input();

  /**
   * Minimum number of FormFields. Default is 1.
   */
  public readonly minItems: InputSignal<number> = input(DEFAULT_MIN_VISIBLE_ITEMS);

  public readonly createFormControl: InputSignal<DynamicFormFieldListCreateFormControl<unknown>> = input.required();

  public readonly formArray: InputSignal<FormArray<FormControl>> = input.required();

  private readonly _formArray$: Observable<FormArray<FormControl>> = toObservable(this.formArray);

  private readonly _isLastItemValid: Signal<boolean> = toSignal(
    this._formArray$.pipe(
      switchMap((formArray: FormArray<FormControl>) =>
        merge(formArray.valueChanges, formArray.statusChanges).pipe(
          startWith(null),
          map(() => (formArray.length > 0 ? formArray.at(-1) : null)),
          distinctUntilChanged(),
          switchMap((lastControl: FormControl | null) => {
            if (!lastControl) return of(false);

            const status$: Observable<boolean> = lastControl.statusChanges.pipe(
              startWith(lastControl.status),
              map((controlStatus: FormControlStatus) => controlStatus === 'VALID'),
              distinctUntilChanged()
            );
            const hasValue$: Observable<boolean> = lastControl.valueChanges.pipe(
              startWith(lastControl.value),
              map((controlValue: unknown) => controlValue !== null && controlValue !== undefined && String(controlValue).trim().length > 0),
              distinctUntilChanged()
            );

            return combineLatest([ status$, hasValue$ ]).pipe(
              map(([ isValid, hasValue ]: [boolean, boolean]) => isValid && hasValue),
              distinctUntilChanged()
            );
          })
        ))
    ),
    { initialValue: false }
  );

  private readonly _visibleItemCounter: WritableSignal<number> = signal(DEFAULT_MIN_VISIBLE_ITEMS);

  // eslint-disable-next-line @unicorn/consistent-function-scoping
  protected readonly canAdd: Signal<boolean> = computed(() => {
    const maxItems: number | undefined = this.maxItems();

    if (maxItems && this._visibleItemCounter() >= maxItems) return false;

    return this._isLastItemValid();
  });

  constructor () {
    this._faIconLibrary.addIcons(faPlusSquare, faMinusSquare);
    effect(() => {
      const maxItems: number | undefined = this.maxItems();
      const minItems: number = this.minItems();
      const formArray: FormArray<FormControl> = this.formArray();

      this._visibleItemCounter.set(formArray.length);

      if (minItems < 1)
        throw new Error('minVisibleItems must be greater than 0');

      if (maxItems !== undefined && maxItems < minItems)
        throw new Error('maxVisibleItems must be greater than minVisibleItems');
    },
    {
      allowSignalWrites: true
    });
  }


  protected addItem (): void {
    if (!this.canAdd()) return;

    const createFormControlCallback: DynamicFormFieldListCreateFormControl<unknown> = this.createFormControl();

    this.formArray().push(createFormControlCallback(this.formArray().length));
    this._visibleItemCounter.update((current: number) => current + 1);
    this.itemAdded.emit();
  }

  protected removeItem (index: number): void {
    this.formArray().removeAt(index, { emitEvent: true });
    this.formArray().markAsDirty();
    this.formArray().updateValueAndValidity({ emitEvent: true });
    this._visibleItemCounter.update((current: number) => current - 1);
    this.itemRemoved.emit(index);
  }
}
